// External Imports
import { useEffect, useState } from "react";
import { MdCheck, MdKey, MdKeyOff, MdRefresh } from "react-icons/md";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { toTitleCase } from "../../util/format-text";
import Heading from "../UI/Heading";
import PersonCard from "../UI/PersonCard";
import SelectPair from "../UI/SelectPair";
import AdminPreset from "./permission-presets/admin.json";
import SalesPreset from "./permission-presets/sales.json";
import PorterPreset from "./permission-presets/porter.json";

/** ========================================================================
 * * EmployeePermissions Component
 * This component renders a form to manage employee permissions.
 * ========================================================================== */

export default function EmployeePermissions() {
  // Get Employee ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [filteredEndpoints, setFilteredEndpoints] = useState([]);
  const [employee, setEmployee] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [updates, setUpdates] = useState({});

  /* ====================== FETCH RECORDS ====================== */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      const ep = await requestAdminAPI("fetch-all", {
        table: "endpoint",
      });
      setEndpoints(ep.sort((a, b) => (a.id > b.id ? 1 : -1)));
      setFilteredEndpoints(ep);
      const e = await requestAdminAPI("fetch", {
        table: "employee",
        id: id,
      });
      setEmployee(e);
      const p = await requestAdminAPI("filter", {
        table: "permission",
        filters: [`employeeId: { eq: "${id}" }`],
      });
      const epMap = {};
      ep.forEach((endpoint) => (epMap[endpoint.id] = false));
      p.forEach((perm) => (epMap[perm.endpointId] = perm.allow));
      setPermissions(epMap);
      setUpdates(epMap);
      document.getElementById("permission-preset").value = "CUSTOM";
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== FILTER PERMISSIONS ====================== */
  const filterPermissions = (query) => {
    if (query === "") setFilteredEndpoints(endpoints);
    const filtered = [];
    endpoints.forEach((end) => {
      let select = false;
      query.split(" ").forEach((keyword) => {
        if (end.id.includes(keyword)) select = true;
      });
      if (select) filtered.push(end);
    });
    setFilteredEndpoints(filtered);
  };

  /* ====================== APPLY PRESET ====================== */
  const applyPreset = (preset) => {
    if (preset === "CUSTOM") {
      // Restore original permissions when Custom is selected
      setUpdates({ ...permissions });
      return;
    }
    if (preset === "ADMIN") setUpdates(AdminPreset);
    if (preset === "SALES") setUpdates(SalesPreset);
    if (preset === "PORTER") setUpdates(PorterPreset);
  };

  /* ====================== EDIT PERMISSIONS ====================== */
  const editPermissionsHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        employeeId: id,
        updates: [],
      };
      Object.keys(updates).forEach((key) => {
        if (updates[key] !== permissions[key])
          payload.updates.push({ endpointId: key, allow: updates[key] });
      });
      await requestAdminAPI("edit-permissions", payload);
      fetchRecords();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  return (
    <div className="container">
      <Heading
        title="Permissions"
        text="Red functions mean not employee is not currently allowed and green means allowed. You can select a preset from the dropdown to quickly assign a set of permissions and click check to save."
      >
        <MdCheck className="icon" onClick={editPermissionsHandler} />
        <MdRefresh className="icon" onClick={fetchRecords} />
      </Heading>

      <div className="flex items-end justify-between text-sm">
        <PersonCard
          link={`/employees/info?id=${employee.id}`}
          image={employee.photo}
          label={employee.email}
          name={employee.name}
          secondary={employee.type}
        />

        <SelectPair
          options={["CUSTOM", "ADMIN", "SALES", "PORTER"]}
          label="Select Preset"
          edit={true}
          inputOptions={{
            onChange: (e) => applyPreset(e.target.value),
            id: "permission-preset",
          }}
        />
      </div>

      <input
        className="w-full border rounded-lg border-primary input"
        type="text"
        autoComplete="off"
        placeholder="Search Permissions..."
        onChange={(e) => filterPermissions(e.target.value)}
      />

      <div className="grid-cols-4 info">
        {loading
          ? Array.from({ length: Math.random() * 10 + 30 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="w-8 h-6 rounded-md bg-stone-600 animate-pulse"></div>
                <div className="w-40 h-6 rounded-md bg-stone-600 animate-pulse"></div>
              </div>
            ))
          : filteredEndpoints.map((endpoint, index) => (
              <div
                key={index}
                className={`flex items-center rounded-md px-4 py-2 clickable ${
                  updates[endpoint.id] || updates[endpoint.id]
                    ? "bg-green-600"
                    : "bg-red-800"
                }`}
                onClick={() =>
                  setUpdates((prev) => ({
                    ...prev,
                    [endpoint.id]: !prev[endpoint.id],
                  }))
                }
              >
                {permissions[endpoint.id] ? (
                  <MdKey className="text-2xl text-white" />
                ) : (
                  <MdKeyOff className="text-2xl text-white" />
                )}
                <p className="w-full mr-3 text-center">
                  {toTitleCase(
                    endpoint.id?.replaceAll("-", " ").replace("/", "")
                  )}
                </p>
              </div>
            ))}
      </div>
    </div>
  );
}
